import { ApplicationRef, DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { filter, switchMap, take, timer } from 'rxjs';

const UPDATE_CHECK_DELAY_MS = 60_000;
const UPDATE_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class PwaUpdateService {
  private readonly appRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly swUpdate = inject(SwUpdate);
  private readonly browserWindow = typeof window !== 'undefined' ? window : null;

  private readonly readyVersion = signal<VersionReadyEvent | null>(null);
  private readonly dismissedVersionHash = signal<string | null>(null);
  private readonly activatingUpdate = signal(false);

  readonly isActivatingUpdate = this.activatingUpdate.asReadonly();
  readonly isUpdateAvailable = computed(() => {
    const readyVersion = this.readyVersion();
    return !!readyVersion && readyVersion.latestVersion.hash !== this.dismissedVersionHash();
  });

  constructor() {
    if (!this.swUpdate.isEnabled) return;

    this.listenForVersionUpdates();
    this.scheduleUpdateChecks();
  }

  dismissUpdate(): void {
    const readyVersion = this.readyVersion();
    if (!readyVersion) return;

    this.dismissedVersionHash.set(readyVersion.latestVersion.hash);
  }

  async activateUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled || !this.readyVersion() || this.activatingUpdate()) return;

    this.activatingUpdate.set(true);

    try {
      await this.swUpdate.activateUpdate();
      this.browserWindow?.location.reload();
    } finally {
      this.activatingUpdate.set(false);
    }
  }

  // Watches Angular service worker events and stores the latest ready version.
  private listenForVersionUpdates(): void {
    this.swUpdate.versionUpdates.pipe(
      filter((event: VersionEvent): event is VersionReadyEvent => event.type === 'VERSION_READY'),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event) => {
      this.readyVersion.set(event);
    });
  }

  // Starts an automatic update check loop once the app is stable and the worker is registered.
  private scheduleUpdateChecks(): void {
    this.appRef.isStable.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => timer(UPDATE_CHECK_DELAY_MS, UPDATE_CHECK_INTERVAL_MS)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      void this.checkForUpdate();
    });
  }

  // Asks the Angular service worker to look for a new app version.
  private async checkForUpdate(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch {
      // A transient network or service-worker error should not interrupt the app flow.
    }
  }
}
